import type { SuggestedTaskResp } from "@/types/suggestedTasks.ts";

/**
 * The logo a suggested task can wear, named rather than drawn: each app keeps
 * its own drawing of these (web's SVG components, native's SVG assets), and
 * both decide which one a task gets here, so a pill on either side shows the
 * same mark for the same task.
 */
export type SuggestedTaskLogo =
  | "gmail"
  | "googleCalendar"
  | "googleDrive"
  | "google"
  | "outlook"
  | "apple"
  | "slack"
  | "teams"
  | "whatsapp"
  | "zoom";

/**
 * Logos by the platform name a task puts in its `{platform}` slot — the one
 * connector hint the catalog sends to clients. Keyed on the display strings as
 * the catalog writes them; a name missing here (Linear) falls back to the
 * category's emoji rather than to a blank.
 */
const PLATFORM_LOGOS: Record<string, SuggestedTaskLogo | undefined> = {
  Gmail: "gmail",
  "Google Calendar": "googleCalendar",
  "Google Drive": "googleDrive",
  "Google Contacts": "google",
  Outlook: "outlook",
  "Outlook calendar": "outlook",
  iPhone: "apple",
  "iPhone calendar": "apple",
  iCloud: "apple",
  Slack: "slack",
  Teams: "teams",
  WhatsApp: "whatsapp",
  Zoom: "zoom",
};

/**
 * The logo of the platform a task is shown running on: the first of its
 * `examplePlatforms`, the same one `fillPlatform` writes into its text.
 */
export const suggestedTaskLogo = (
  task: Pick<SuggestedTaskResp, "examplePlatforms">,
): SuggestedTaskLogo | undefined => {
  const [platform] = task.examplePlatforms;
  return platform ? PLATFORM_LOGOS[platform] : undefined;
};

/**
 * Neutral enough to sit beside the others without looking like a placeholder,
 * so a task the catalog gave no emoji looks like a task.
 */
const FALLBACK_EMOJI = "💡";

/**
 * The emoji a task wears when it has no logo: its own, then its category's.
 *
 * Both come down with the sample rather than from a table here. A table keyed
 * on category ids goes stale the moment the catalog is reorganized, and does
 * it silently: every pill keeps rendering, all of them the fallback.
 */
export const suggestedTaskEmoji = (
  task: Pick<SuggestedTaskResp, "emoji">,
  categoryEmoji: string | undefined,
): string => task.emoji ?? categoryEmoji ?? FALLBACK_EMOJI;
